/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import type { AgencyStatus } from '@/features/onboarding/types/agency-dashboard';

/**
 * How an inactive subscription restricts the agency shell.
 *
 * - `none`: the shell behaves normally.
 * - `draft`: there is no subscription yet, so the agency never opens — the
 *   activation screen takes over the whole viewport, outside the shell.
 * - `read-only`: suspended. The shell renders, but only the overview carries
 *   content.
 * - `cancelled`: same restriction as `read-only`, with the reactivation screen
 *   standing in for the overview.
 */
export type SubscriptionLock = 'none' | 'draft' | 'read-only' | 'cancelled';

/**
 * Route segments that stay reachable while the subscription is locked.
 *
 * The settings are not among them: an unpaid agency is closed everywhere but
 * its overview, and the one thing it still needs from the settings — putting
 * a working card on file — is handed to it by the regularisation modal, over
 * the overview, without opening an area full of controls it cannot use.
 */
export const UNLOCKED_SEGMENTS = ['dashboard', 'subscribe'] as const;

/** Statuses that leave the shell untouched. */
const RUNNING_STATUSES: readonly AgencyStatus[] = ['active', 'trialing', 'pending_upgrade'];

const LOCK_BY_STATUS: Partial<Record<AgencyStatus, SubscriptionLock>> = {
  no_subscription: 'draft',
  payment_failed: 'read-only',
  suspended: 'read-only',
  cancelled: 'cancelled',
};

/**
 * Whether the subscription is running, so the office opens and asks its owner
 * for nothing.
 *
 * The trial is one of these: it is a subscription under way, not a signup left
 * half-done. Every screen that sorts offices into "running" and "needs you"
 * reads this, so the two cannot answer the question differently.
 *
 * An absent status is not running: the caller has an office whose state has
 * not arrived, and claiming it is running would open a door that may be shut.
 * {@link lockForStatus} handles that case on its own terms.
 *
 * @param status - The agency's effective subscription status.
 * @returns True while the subscription is active, trialing or upgrading.
 */
export function isRunningStatus(status?: AgencyStatus | null): boolean {
  return status ? RUNNING_STATUSES.includes(status) : false;
}

/**
 * Reads the restriction a subscription status imposes on the agency shell.
 *
 * An absent status means the agency has not loaded yet, which must not block
 * anything: the shell is rendered while the request is still in flight.
 *
 * @param status - The agency's effective subscription status.
 * @returns The lock to apply.
 */
export function lockForStatus(status?: AgencyStatus | null): SubscriptionLock {
  if (!status || isRunningStatus(status)) return 'none';
  return LOCK_BY_STATUS[status] ?? 'none';
}

/**
 * Whether the given lock keeps the shell on screen with most areas closed.
 *
 * @param lock - The lock in effect.
 * @returns True for the suspended and cancelled locks.
 */
export function isShellRestricted(lock: SubscriptionLock): boolean {
  return lock === 'read-only' || lock === 'cancelled';
}

/**
 * Whether a path stays reachable under a restricted shell.
 *
 * @param pathname - The current route pathname.
 * @returns True when the path is one of {@link UNLOCKED_SEGMENTS}.
 */
export function isUnlockedPath(pathname: string): boolean {
  const trimmed = pathname.replace(/\/+$/, '');
  return UNLOCKED_SEGMENTS.some((segment) => trimmed.endsWith(`/${segment}`));
}

/**
 * Whether the path is the subscribe flow itself.
 *
 * A draft agency closes every route but this one: the activation screen is
 * the agency, and the settings it would otherwise offer belong to an agency
 * that has already opened.
 *
 * @param pathname - The current route pathname.
 * @returns True on the subscribe route.
 */
export function isSubscribeRoute(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').endsWith('/subscribe');
}

/**
 * Whether a sidebar entry should be drawn as locked.
 *
 * @param lock - The lock in effect.
 * @param segment - The nav item's route segment, when it has one.
 * @returns True when the entry leads somewhere the lock closed.
 */
export function isSegmentLocked(lock: SubscriptionLock, segment?: string): boolean {
  if (!isShellRestricted(lock)) return false;
  return segment !== 'dashboard';
}
